// ---------------------------------------------------------------------------
// Test Mode — simulated suppliers (10) for Clean Code Culture SRL (CLE-250).
//
// These recurring vendors map directly to the fixed monthly expenses in the
// financials module, keeping the P&L expense side coherent with the supplier
// list. Monthly costs are documented in EUR in the issue; we store them in RON.
// ---------------------------------------------------------------------------

import type { Supplier, SupplierCategory } from '../lib/suppliersApi';
import { isoDateTime, eur } from './company';
import { testCompanyTag } from './companies';

export interface SupplierSeed {
  id: string;
  name: string;
  cui: string;
  category: SupplierCategory;
  monthlyEur: number; // 0 = non-recurring / quarterly handled in notes
  isRecurring: boolean;
  notes: string;
}

export const SUPPLIER_SEEDS: SupplierSeed[] = [
  {
    id: 's1',
    name: 'AWS România',
    cui: 'RO99001122',
    category: 'saas',
    monthlyEur: 800,
    isRecurring: true,
    notes: 'Hosting cloud (EC2, S3, RDS).',
  },
  {
    id: 's2',
    name: 'ContaPlus SRL',
    cui: 'RO88112233',
    category: 'subcontractor',
    monthlyEur: 500,
    isRecurring: true,
    notes: 'Servicii de contabilitate lunară.',
  },
  {
    id: 's3',
    name: 'Avocații Asociați',
    cui: 'RO77223344',
    category: 'subcontractor',
    monthlyEur: 300,
    isRecurring: true,
    notes: 'Consultanță juridică retainer.',
  },
  {
    id: 's4',
    name: 'JetBrains / Figma / Slack / GitHub',
    cui: 'RO66334455',
    category: 'saas',
    monthlyEur: 600,
    isRecurring: true,
    notes: 'Licențe software dezvoltare și colaborare.',
  },
  {
    id: 's5',
    name: 'DigitalAds SRL',
    cui: 'RO55445566',
    category: 'other',
    monthlyEur: 1000,
    isRecurring: true,
    notes: 'Campanii marketing și performance.',
  },
  {
    id: 's6',
    name: 'Cowork Cluj',
    cui: 'RO44556677',
    category: 'office',
    monthlyEur: 1500,
    isRecurring: true,
    notes: 'Chirie spațiu de birou (18 persoane).',
  },
  {
    id: 's7',
    name: 'Orange Business',
    cui: 'RO33667788',
    category: 'utilities',
    monthlyEur: 200,
    isRecurring: true,
    notes: 'Telecomunicații și internet business.',
  },
  {
    id: 's8',
    name: 'Allianz',
    cui: 'RO22778899',
    category: 'other',
    monthlyEur: 150,
    isRecurring: true,
    notes: 'Asigurări de răspundere profesională.',
  },
  {
    id: 's9',
    name: 'FAN Courier',
    cui: 'RO11889900',
    category: 'utilities',
    monthlyEur: 100,
    isRecurring: true,
    notes: 'Servicii de curierat.',
  },
  {
    id: 's10',
    name: 'DevAcademy',
    cui: 'RO10990011',
    category: 'other',
    monthlyEur: 167,
    isRecurring: false,
    notes: 'Training echipă — 500 EUR / trimestru.',
  },
];

// Settings-managed supplier category nomenclature. Duplicated locally (instead
// of importing DEFAULT_SUPPLIER_CATEGORIES from lib/suppliersApi) to avoid a
// runtime import cycle: api -> mockRouter -> testData -> suppliersApi -> api.
export function buildSupplierCategories(): { code: string; label: string }[] {
  return [
    { code: 'saas', label: 'SaaS / Software' },
    { code: 'utilities', label: 'Utilities' },
    { code: 'office', label: 'Office' },
    { code: 'subcontractor', label: 'Subcontractor' },
    { code: 'other', label: 'Other' },
  ];
}

// Total recurring monthly supplier expense in RON — consumed by financials to
// keep the operational expense line consistent.
export const MONTHLY_SUPPLIER_EXPENSE_RON = SUPPLIER_SEEDS.reduce(
  (sum, s) => sum + eur(s.monthlyEur),
  0,
);

const CREATED_AT = isoDateTime(-500, 10);
const UPDATED_AT = isoDateTime(-7, 12);

export function buildSuppliers(): Supplier[] {
  return SUPPLIER_SEEDS.map((s, i) => ({
    id: s.id,
    name: s.name,
    cui: s.cui,
    iban: `RO49AAAA1B31007593840000${String(i).padStart(2, '0')}`,
    contactPerson: 'Departament contracte',
    email: `contracte@${s.name
      .split(' ')[0]
      .toLowerCase()
      .replace(/[^a-z]/g, '')}.com`,
    phone: '+40 264 000 000',
    address: 'România',
    category: s.category,
    isRecurring: s.isRecurring,
    expenseCategoryId: null,
    notes: `${s.notes} (~${s.monthlyEur} EUR/lună)`,
    isActive: true,
    ...testCompanyTag(s.id),
    createdAt: CREATED_AT,
    updatedAt: UPDATED_AT,
  }));
}

export function buildSupplier(id: string): Supplier | null {
  return buildSuppliers().find((s) => s.id === id) ?? null;
}

// --- Supplier resources (flat list of documents) ---------------------------
// Must match SupplierResource from lib/suppliersApi.ts.

import type { SupplierResource } from '../lib/suppliersApi';

export function buildSupplierResources(supplierId: string): SupplierResource[] {
  const seed = SUPPLIER_SEEDS.find((s) => s.id === supplierId);
  if (!seed) return [];

  const resources: SupplierResource[] = [
    {
      id: `${supplierId}-res-contract`,
      supplierId,
      name: `Contract furnizor — ${seed.name}.pdf`,
      description: null,
      category: 'Contracte',
      fileType: 'PDF',
      fileSize: 204_800,
      s3Key: `suppliers/${supplierId}/contract.pdf`,
      s3Url: null,
      uploadedAt: isoDateTime(-400, 10),
    },
    {
      id: `${supplierId}-res-invoice1`,
      supplierId,
      name: `Factură ${seed.name} — luna trecută.pdf`,
      description: null,
      category: 'Facturi',
      fileType: 'PDF',
      fileSize: 89_000,
      s3Key: `suppliers/${supplierId}/factura-1.pdf`,
      s3Url: null,
      uploadedAt: isoDateTime(-30, 10),
    },
    {
      id: `${supplierId}-res-invoice2`,
      supplierId,
      name: `Factură ${seed.name} — luna curentă.pdf`,
      description: null,
      category: 'Facturi',
      fileType: 'PDF',
      fileSize: 91_000,
      s3Key: `suppliers/${supplierId}/factura-2.pdf`,
      s3Url: null,
      uploadedAt: isoDateTime(-5, 10),
    },
  ];

  // Recurring suppliers get an extra order document.
  if (seed.isRecurring) {
    resources.push({
      id: `${supplierId}-res-order`,
      supplierId,
      name: `Comandă recurentă — ${seed.name}.pdf`,
      description: null,
      category: 'Comenzi',
      fileType: 'PDF',
      fileSize: 64_000,
      s3Key: `suppliers/${supplierId}/comanda.pdf`,
      s3Url: null,
      uploadedAt: isoDateTime(-200, 10),
    });
  }

  return resources;
}
